// pkg198 — Automatic payments: autopay_timing, autopay_days_after_issue (0–90), retainer_replenishment_enabled. Owner PATCH. Does not change LawPay live mode — delivered 2026-11-01
// pkg171a — Client Payments (LawPay) card mounted between Billing and Export Firm Data (LawPayConnectCard; owner page, so the card's own view_billing read always passes) — delivered 2026-10-02
// pkg90 — Export Firm Data card (D-90g): owner-visible (hidden on the staff 403 — no role plumbing), start/poll/download/regenerate against /api/firm-export; free, one per day, 24h link, honest regenerate-on-miss when a deploy clears the file — delivered 2026-07-18
// pkg74 — save flash reports the jurisdiction cascade ("Added TN: N modules enabled, M assignments across K clients", D-74f); jurisdiction chips converted green→pale-navy per the pkg72a decorative-green convention — delivered 2026-07-13
// pkg68 — the pkg65 Billing card is replaced by a pointer to the new Firm Billing + Firm Wallet pages; ?billing= return flags are forwarded to the right page (deploy-window + old-bookmark safety, D-68g) — delivered 2026-07-12
// pkg65 — Billing card (owner-only page): plan/status/renewal, AI free-action + credit balance, Subscribe / Buy Credits / Manage Billing via Stripe; handles ?billing= return flags (D-65i) — delivered 2026-07-11
// pkg63 — Jurisdictions card: owner-editable list of states the firm practices in (≥1 required); controls which state-specific built-in modules the firm receives — delivered 2026-07-10
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom'; // pkg68 — pointer card links
import { adminApi } from '../utils/adminApi';
import { useFirmSettings } from '../contexts/FirmSettingsContext';
import LawPayConnectCard from '../components/LawPayConnectCard'; // pkg171a

// pg may return autopay_days_after_issue as a numeric string. The PATCH
// handler requires a whole number 0–90 and a real boolean, including false.
function withAutopayDefaults(data) {
  const src = data || {};
  const days = Number(src.autopay_days_after_issue);
  return {
    ...src,
    autopay_timing: src.autopay_timing === 'days_after_issue' ? 'days_after_issue' : 'due_date',
    autopay_days_after_issue: Number.isInteger(days) ? days : 0,
    retainer_replenishment_enabled: src.retainer_replenishment_enabled === true,
  };
}

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
];

export default function AdminFirmSettingsPage() {
  const { refresh } = useFirmSettings();
  const [form,    setForm]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    adminApi.getFirmSettings()
      .then(data => { setForm(withAutopayDefaults(data)); setLoading(false); })
      .catch(err  => { setError(err.message); setLoading(false); });
  }, []);

  // pkg68 (D-68g) — the pkg65 Billing card moved to the dedicated Firm
  // Billing and Firm Wallet pages. This page keeps ONE job from that era:
  // forwarding any ?billing= Stripe-return flags to the right new page, so
  // (a) checkout sessions started before this deploy still land somewhere
  // that handles them, and (b) old bookmarks keep working.
  useEffect(() => {
    const flag = new URLSearchParams(window.location.search).get('billing');
    if (!flag) return;
    const dest = flag === 'credits-success'
      ? '/admin/dashboard/firm-wallet'
      : '/admin/dashboard/firm-billing';
    window.location.replace(`${dest}?billing=${encodeURIComponent(flag)}`);
  }, []);

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); }

  // pkg90 — Export Firm Data (owner-visible card, D-90g). Non-owners get a
  // 403 from the adminAuth catch-all on the status read, which simply hides
  // the card — no role plumbing needed, and the master-key session works.
  const [exp,        setExp]        = useState(null);   // latest export row (or null)
  const [expVisible, setExpVisible] = useState(false);
  const [expBusy,    setExpBusy]    = useState(false);
  const [expError,   setExpError]   = useState('');

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    async function load() {
      try {
        const data = await adminApi.firmExportStatus();
        if (cancelled) return;
        setExpVisible(true);
        const latest = (data.exports && data.exports[0]) || null;
        setExp(latest);
        // Poll while a job is in flight so Ready appears without a refresh.
        if (latest && (latest.status === 'queued' || latest.status === 'running')) {
          timer = setTimeout(load, 5000);
        }
      } catch (err) {
        if (cancelled) return;
        if (err.status === 403) setExpVisible(false); // staff — card hidden
        else { setExpVisible(true); setExpError(err.message); }
      }
    }
    load();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, []);

  async function startExport(regenerateId) {
    setExpBusy(true); setExpError('');
    try {
      const data = regenerateId
        ? await adminApi.firmExportRegenerate(regenerateId)
        : await adminApi.firmExportRun();
      setExp(data.export);
      // Kick the poller by reloading status after a beat.
      setTimeout(async () => {
        try {
          const s = await adminApi.firmExportStatus();
          const latest = (s.exports && s.exports[0]) || null;
          setExp(latest);
        } catch (_) { /* next manual action reloads */ }
      }, 4000);
    } catch (err) { setExpError(err.message); }
    finally { setExpBusy(false); }
  }

  async function downloadExport() {
    if (!exp || !exp.download_token) return;
    setExpBusy(true); setExpError('');
    try {
      const blob = await adminApi.firmExportDownload(exp.id, exp.download_token);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `lawmighty-firm-export-${new Date(exp.completed_at || Date.now()).toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExpError(err.message);
      // The file may have been cleared by a deploy — refresh status so the
      // card flips to Regenerate honestly.
      try {
        const s = await adminApi.firmExportStatus();
        setExp((s.exports && s.exports[0]) || null);
      } catch (_) { /* leave the error visible */ }
    }
    finally { setExpBusy(false); }
  }

  // pkg63 — jurisdictions chip helpers. The backend rejects an empty list,
  // so removal is blocked client-side once only one state remains.
  const juris = Array.isArray(form?.jurisdictions) ? form.jurisdictions : [];
  function addJurisdiction(code) {
    if (!code || juris.includes(code)) return;
    set('jurisdictions', [...juris, code]);
  }
  function removeJurisdiction(code) {
    if (juris.length <= 1) return;
    set('jurisdictions', juris.filter(j => j !== code));
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess('');
    const days = Number(form.autopay_days_after_issue);
    if (!Number.isInteger(days) || days < 0 || days > 90) {
      setSaving(false);
      setError('Autopay days after issue must be a whole number from 0 to 90.');
      return;
    }
    const payload = {
      ...form,
      autopay_timing: form.autopay_timing === 'days_after_issue' ? 'days_after_issue' : 'due_date',
      autopay_days_after_issue: days,
      retainer_replenishment_enabled: form.retainer_replenishment_enabled === true,
    };
    try {
      const updated = await adminApi.saveFirmSettings(payload);
      // pkg74 (D-74f) — the backend reports what a jurisdiction ADDITION
      // just did (modules enabled for the firm + assignments added across
      // existing clients). Strip it from form state so it never rides back
      // on the next save.
      const { jurisdiction_cascade: cascade, ...settings } = updated || {};
      setForm(withAutopayDefaults(settings));
      // Push the new values into the global firm settings context so the
      // sidebar, login pages, and any other components update immediately.
      await refresh();
      if (cascade && (cascade.modules_enabled || cascade.client_assignments)) {
        const states = (cascade.added_states || []).join(', ');
        const parts = [];
        if (cascade.modules_enabled) {
          parts.push(`${cascade.modules_enabled} module${cascade.modules_enabled === 1 ? '' : 's'} enabled for your firm`);
        }
        if (cascade.client_assignments) {
          parts.push(`${cascade.client_assignments} module assignment${cascade.client_assignments === 1 ? '' : 's'} added across ${cascade.clients_touched} client${cascade.clients_touched === 1 ? '' : 's'}`);
        }
        setSuccess(`Firm settings saved. Added ${states}: ${parts.join('; ')}.`);
      } else if (cascade) {
        // States were added but nothing new matched (e.g. no modules are
        // tagged to that state yet) — still worth confirming the add took.
        setSuccess(`Firm settings saved. Added ${(cascade.added_states || []).join(', ')} — no additional modules apply.`);
      } else {
        setSuccess('Firm settings saved successfully.');
      }
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  if (loading) return <div style={{ padding: 40, fontFamily: 'Arial, sans-serif', color: '#888' }}>Loading...</div>;

  return (
    <div>
      <h1 className="page-title">Firm Settings</h1>
      <p className="page-subtitle">
        This information appears on generated documents, client emails, and the client portal.
        Only owners can edit these settings.
      </p>

      {error   && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* pkg68 (D-68g) — pointer to the dedicated pages that replaced the
          pkg65 Billing card. */}
      <div className="section-head" style={{ marginTop: 0 }}>Billing</div>
      <div className="card">
        <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#333', margin: 0 }}>
          Your subscription, payment method, and receipts moved to{' '}
          <Link to="/admin/dashboard/firm-billing">Firm Billing</Link>; AI credits and the
          transaction history live on <Link to="/admin/dashboard/firm-wallet">Firm Wallet</Link>.
        </p>
      </div>

      {/* pkg171a — Client Payments (LawPay): lane B, client → firm legal
          fees. Distinct from the Billing pointer above (lane A, Stripe). */}
      <LawPayConnectCard />

      {/* pkg90 (D-90g) — Export Firm Data: owner-visible administrative card
          (free — it honors ToS §10/§16, so it lives here, not the wallet). */}
      {expVisible && (
        <>
          <div className="section-head">Export Firm Data</div>
          <div className="card">
            <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#333', marginTop: 0 }}>
              Generate a complete export of your firm's data — clients, contacts, invoices and
              line items, time, expenses, payments, the trust ledger, activity log, staff list,
              every submission and assembled document, and AI reports — as one zip file. Free,
              once per day. The download link stays valid for 24 hours.
            </p>
            {expError && <div className="alert alert-error">{expError}</div>}
            {exp && (
              <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, margin: '0 0 12px 0' }}>
                {exp.status === 'queued'  && <>Export queued&hellip;</>}
                {exp.status === 'running' && <>Export in progress &mdash; this can take several minutes for large firms&hellip;</>}
                {exp.status === 'failed'  && <span style={{ color: '#b00020' }}>Last export failed: {exp.error || 'unknown error'}</span>}
                {exp.status === 'ready'   && (
                  exp.download_available
                    ? <>Export ready ({exp.file_bytes ? `${Math.max(1, Math.round(exp.file_bytes / 1024))} KB` : 'zip'}), generated {new Date(exp.completed_at).toLocaleString('en-US')} &mdash; link expires {new Date(exp.expires_at).toLocaleString('en-US')}.</>
                    : <>A previous export from {new Date(exp.completed_at).toLocaleString('en-US')} is no longer downloadable (the link expired or the file was cleared by a deploy).</>
                )}
              </p>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {exp && exp.status === 'ready' && exp.download_available && (
                <button type="button" className="btn btn-primary" disabled={expBusy} onClick={downloadExport}>
                  {expBusy ? <><span className="spinner" />Working&hellip;</> : 'Download Export'}
                </button>
              )}
              {exp && exp.status === 'ready' && exp.needs_regenerate && (
                <button type="button" className="btn btn-primary" disabled={expBusy} onClick={() => startExport(exp.id)}>
                  {expBusy ? <><span className="spinner" />Working&hellip;</> : 'Regenerate Export'}
                </button>
              )}
              {(!exp || exp.status === 'failed' || (exp.status === 'ready' && exp.download_available)) && (
                <button type="button" className="btn" disabled={expBusy || (exp && (exp.status === 'queued' || exp.status === 'running'))} onClick={() => startExport(null)}>
                  {expBusy ? <><span className="spinner" />Working&hellip;</> : 'Start New Export'}
                </button>
              )}
            </div>
          </div>
        </>
      )}

      <form onSubmit={handleSave}>

        {/* Firm Information */}
        <div className="section-head">Firm Information</div>
        <div className="card">
          <div className="form-row">
            <div className="form-group">
              <label>Firm Name</label>
              <input type="text" value={form.firm_name || ''}
                onChange={e => set('firm_name', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Client Contact Email</label>
              <input type="email" value={form.client_contact_email || ''}
                onChange={e => set('client_contact_email', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Phone</label>
              <input type="tel" value={form.phone || ''}
                onChange={e => set('phone', e.target.value)}
                placeholder="(555) 123-4567" />
            </div>
            <div className="form-group">
              <label>Fax</label>
              <input type="tel" value={form.fax || ''}
                onChange={e => set('fax', e.target.value)}
                placeholder="Optional" />
            </div>
            <div className="form-group">
              <label>Website</label>
              <input type="url" value={form.website || ''}
                onChange={e => set('website', e.target.value)}
                placeholder="https://yourfirm.com" />
            </div>
          </div>
        </div>

        {/* Jurisdictions — pkg63 */}
        <div className="section-head">Jurisdictions</div>
        <div className="card">
          <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555', marginTop: 0, marginBottom: 12 }}>
            The states where your firm practices. State-specific built-in modules (for example,
            Tennessee's Parenting Plan and Child Support Calculator) only appear for firms with
            that state listed here. Removing a state hides its modules without deleting any
            client data — adding it back restores them. At least one state is required.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
            {juris.map(code => (
              <span key={code} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 13, fontFamily: 'Arial, sans-serif', fontWeight: 600,
                padding: '4px 10px', borderRadius: 14,
                background: '#F0F2F8', color: '#1E2761', border: '1px solid #1E276133',
              }}>
                {code}
                <button type="button" onClick={() => removeJurisdiction(code)}
                  disabled={juris.length <= 1}
                  title={juris.length <= 1 ? 'At least one state is required' : `Remove ${code}`}
                  aria-label={`Remove ${code}`}
                  style={{ border: 'none', background: 'none',
                    cursor: juris.length <= 1 ? 'default' : 'pointer',
                    color: juris.length <= 1 ? '#aaa' : '#1E2761',
                    fontSize: 14, lineHeight: 1, padding: 0 }}>
                  ×
                </button>
              </span>
            ))}
            {juris.length === 0 && (
              <span style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#c62828' }}>
                No jurisdictions set — add at least one state below.
              </span>
            )}
          </div>
          <div className="form-group" style={{ maxWidth: 220, marginBottom: 0 }}>
            <select value="" onChange={e => addJurisdiction(e.target.value)}>
              <option value="">Add a state…</option>
              {US_STATES.filter(s => !juris.includes(s)).map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Business Address */}
        <div className="section-head">Business Address</div>
        <div className="card">
          <div className="form-group">
            <label>Street</label>
            <input type="text" value={form.business_street || ''}
              onChange={e => set('business_street', e.target.value)}
              placeholder="123 Main Street, Suite 100" />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>City</label>
              <input type="text" value={form.business_city || ''}
                onChange={e => set('business_city', e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: '0 0 100px' }}>
              <label>State</label>
              <select value={form.business_state || 'TN'}
                onChange={e => set('business_state', e.target.value)}>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ flex: '0 0 120px' }}>
              <label>ZIP Code</label>
              <input type="text" value={form.business_zip || ''}
                onChange={e => set('business_zip', e.target.value)} />
            </div>
          </div>
        </div>

        {/* Mailing Address */}
        <div className="section-head">Mailing Address</div>
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <input type="checkbox"
              checked={
                form.mailing_street === form.business_street &&
                form.mailing_city  === form.business_city &&
                form.mailing_state === form.business_state &&
                form.mailing_zip   === form.business_zip
              }
              onChange={e => {
                if (e.target.checked) {
                  setForm(p => ({ ...p,
                    mailing_street: p.business_street,
                    mailing_city:   p.business_city,
                    mailing_state:  p.business_state,
                    mailing_zip:    p.business_zip,
                  }));
                }
              }}
              id="same-as-business"
            />
            <label htmlFor="same-as-business" style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, cursor: 'pointer' }}>
              Same as business address
            </label>
          </div>
          <div className="form-group">
            <label>Street</label>
            <input type="text" value={form.mailing_street || ''}
              onChange={e => set('mailing_street', e.target.value)} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>City</label>
              <input type="text" value={form.mailing_city || ''}
                onChange={e => set('mailing_city', e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: '0 0 100px' }}>
              <label>State</label>
              <select value={form.mailing_state || 'TN'}
                onChange={e => set('mailing_state', e.target.value)}>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ flex: '0 0 120px' }}>
              <label>ZIP Code</label>
              <input type="text" value={form.mailing_zip || ''}
                onChange={e => set('mailing_zip', e.target.value)} />
            </div>
          </div>
        </div>

        {/* pkg198 — firm autopay schedule and the trust-replenishment master switch.
            Saved methods and plans stay in LawPay test mode; this form does not
            call the live-mode switch. */}
        <div className="section-head">Automatic payments</div>
        <div className="card">
          <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555', marginTop: 0 }}>
            These settings decide when autopay charges a new invoice, and whether an owner or attorney
            may set a trust replenishment on a client. Saved payment methods, autopay, and payment plans
            run only while LawPay is in test mode. Saving here does not turn on live mode.
          </p>
          <label style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <input type="radio" name="autopay_timing" checked={form.autopay_timing !== 'days_after_issue'}
              onChange={() => set('autopay_timing', 'due_date')} />
            Charge new invoices on the due date
          </label>
          <label style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <input type="radio" name="autopay_timing" checked={form.autopay_timing === 'days_after_issue'}
              onChange={() => set('autopay_timing', 'days_after_issue')} />
            Charge new invoices a number of days after the issue date
          </label>
          {form.autopay_timing === 'days_after_issue' && (
            <div className="form-group" style={{ maxWidth: 220 }}>
              <label>Days after the issue date (0–90)</label>
              <input type="number" min="0" max="90" step="1" value={form.autopay_days_after_issue}
                onWheel={e => e.currentTarget.blur()}
                onChange={e => set('autopay_days_after_issue', e.target.value === '' ? '' : parseInt(e.target.value, 10))} />
            </div>
          )}
          <label style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 8 }}>
            <input type="checkbox" checked={form.retainer_replenishment_enabled === true}
              onChange={e => set('retainer_replenishment_enabled', e.target.checked)}
              style={{ marginTop: 2 }} />
            <span>
              Allow retainer replenishment. An owner or attorney then sets the threshold and amount
              on the client’s Billing tab. A replenishment runs only when this switch is on, that
              client’s replenishment is on, and the client has autopay on with a saved card. The
              charge deposits to trust.
            </span>
          </label>
        </div>

        {/* Format Settings */}
        <div className="section-head">Format Settings</div>
        <div className="card">
          <div className="form-row">
            <div className="form-group">
              <label>Date Format</label>
              <select value={form.date_format || 'MM/DD/YYYY'}
                onChange={e => set('date_format', e.target.value)}>
                <option value="MM/DD/YYYY">12/31/2026</option>
                <option value="DD/MM/YYYY">31/12/2026</option>
                <option value="YYYY-MM-DD">2026-12-31</option>
                <option value="DD.MM.YYYY">31.12.2026</option>
              </select>
            </div>
            <div className="form-group">
              <label>Time Format</label>
              <select value={form.time_format || '12h'}
                onChange={e => set('time_format', e.target.value)}>
                <option value="12h">11:59 PM</option>
                <option value="12h_lower">11:59 pm</option>
                <option value="24h">23:59</option>
              </select>
            </div>
            <div className="form-group">
              <label>Number Format</label>
              <select value={form.number_format || 'USD'}
                onChange={e => set('number_format', e.target.value)}>
                <option value="USD">$1,234.56</option>
                <option value="EUR_DOT">1.234,56 $</option>
                <option value="EUR_SPACE">1 234,56 $</option>
              </select>
            </div>
          </div>
        </div>

        {/* Last updated */}
        {form.updated_at && (
          <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#aaa', marginBottom: 16 }}>
            Last updated: {new Date(form.updated_at).toLocaleString('en-US')}
          </p>
        )}

        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? <><span className="spinner" />Saving...</> : 'Save Firm Settings'}
        </button>

      </form>
    </div>
  );
}
